import { useCallback } from "react";

import { useTranslation } from "@/lib/i18n";
import { NAVIGATION_ELEMENT_KIND } from "@/lib/navigation-element-kinds";
import {
  toolbarLabelWithCode,
  warningPageNavLabel,
  type WarningPageCopy,
  type WarningPageNavDefaults,
} from "@/lib/warning-page-copy";

/** Locale defaults that `PublicWarningSettings.*_override` replaces. */
export function usePublicWarningPageDefaults(): WarningPageCopy {
  const { t } = useTranslation();
  return {
    title: t("riskPicture.defaults.title"),
    description: t("riskPicture.defaults.description"),
    toolbarLabel: t("riskPicture.defaults.toolbar"),
  };
}

/** Locale defaults that `MaritimeWarningSettings.*_override` replaces. */
export function useMaritimeWarningPageDefaults(): WarningPageCopy {
  const { t } = useTranslation();
  return {
    title: t("dashboard.warnings.maritime.defaults.title"),
    description: t("dashboard.warnings.maritime.defaults.description"),
    toolbarLabel: t("dashboard.warnings.maritime.defaults.toolbar"),
  };
}

/**
 * Toolbar button text: the configured label (a prefix like "Land") plus the highest
 * active level, e.g. "Land: Code oranje". The level is never typed by admins.
 */
export function useToolbarLabelWithCode() {
  const { t } = useTranslation();
  return useCallback(
    (label: string, code: string) =>
      toolbarLabelWithCode(label, code, (prefix, level) =>
        t("dashboard.warnings.toolbarLabel", { label: prefix, code: level }),
      ),
    [t],
  );
}

/**
 * Nav label of a built-in warning page: the navigation element title (editable in the
 * pages overview), else the locale default for its kind. Other kinds keep their title.
 */
export function useWarningPageNavLabel() {
  const { t } = useTranslation();
  return useCallback(
    (element: Parameters<typeof warningPageNavLabel>[0]): string => {
      const defaults: WarningPageNavDefaults = {
        [NAVIGATION_ELEMENT_KIND.publicWarningsPage]: t("riskPicture.defaults.nav"),
        [NAVIGATION_ELEMENT_KIND.maritimeWarningsPage]: t(
          "dashboard.warnings.maritime.defaults.nav",
        ),
      };
      return warningPageNavLabel(element, defaults);
    },
    [t],
  );
}
